import React, { useEffect, useState } from "react";
import { Anchor, Brain, Database, X, Clock, Zap, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { getChatContext, forgetMemory } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";

function useBodyLock(locked) {
  useEffect(() => {
    if (!locked) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [locked]);
}

function fmtTime(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function ContextChip({ sessionId, context, onChanged }) {
  const { user } = useAuth();
  const isArchitect = !!user?.is_architect;
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [forgetting, setForgetting] = useState(null);

  useBodyLock(open);

  const cp = context || {};
  const exchanges = cp.sliding_window_exchanges ?? 0;
  const cap = cp.sliding_window_cap ? Math.floor(cp.sliding_window_cap / 2) : 10;
  const tokens = cp.session_tokens_total ?? 0;
  const interval = cp.pulse_interval || 2500;
  const untilPulse = Math.max(0, interval - (tokens % interval));
  const vectorTotal = cp.vector_memory_total ?? 0;
  const vectorHits = cp.vector_memory_hits || [];

  const openDrawer = async () => {
    setOpen(true);
    setLoading(true);
    try {
      const d = await getChatContext(sessionId);
      setDetail(d);
      onChanged?.(d);
    } catch {
      setDetail(null);
    } finally {
      setLoading(false);
    }
  };

  const [localHits, setLocalHits] = useState(null);
  useEffect(() => { setLocalHits(null); }, [context]);
  const hitsForDisplay = localHits ?? vectorHits;

  const handleForget = async (memoryId) => {
    if (!isArchitect) return;
    setForgetting(memoryId);
    try {
      await forgetMemory(memoryId);
      toast.success("Drift Neutralization Event", {
        description: `Memory ${memoryId.slice(0, 32)}… forgotten.`,
      });
      // Optimistically remove from the on-screen list for this chip
      setLocalHits((hitsForDisplay || []).filter((h) => h.id !== memoryId));
    } catch (err) {
      toast.error("Forget failed", {
        description:
          err?.response?.data?.detail || "Memory not found or not permitted.",
      });
    } finally {
      setForgetting(null);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={openDrawer}
        data-testid="context-chip"
        className="w-full flex items-center justify-between gap-3 px-4 py-2 border border-emerald-500/40 bg-[#0a1511]/80 text-emerald-400 hover:border-emerald-500 hover:bg-emerald-500/10 text-[10px] uppercase tracking-[0.25em] transition-colors"
      >
        <span className="flex items-center gap-2 min-w-0">
          <Anchor className="h-3 w-3 shrink-0" />
          <span className="truncate">
            Context: {exchanges}/{cap} exchanges · Anchor{" "}
            <span className="text-emerald-300">LOCKED</span>
          </span>
        </span>
        <span className="flex items-center gap-3 shrink-0 text-zinc-400 tabular-nums">
          <span className="flex items-center gap-1"><Zap className="h-3 w-3" /> {tokens}t</span>
          <span className="hidden sm:flex items-center gap-1"><Clock className="h-3 w-3" /> {untilPulse}→pulse</span>
          <span className="flex items-center gap-1"><Brain className="h-3 w-3" /> {vectorHits.length}/{vectorTotal}</span>
        </span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
          onClick={() => setOpen(false)}
          data-testid="context-chip-overlay"
        >
          <div
            className="w-full sm:max-w-3xl max-h-[92vh] overflow-auto bg-[#0b0b0d] border border-zinc-800 text-zinc-100"
            onClick={(e) => e.stopPropagation()}
            data-testid="context-chip-drawer"
          >
            <div className="flex items-center justify-between p-4 border-b border-zinc-900">
              <div>
                <div className="text-[10px] uppercase tracking-[0.35em] text-emerald-400">
                  Context Dashboard
                </div>
                <div className="font-heading text-xl mt-0.5">What the Sentinel sees</div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="p-2 text-zinc-400 hover:text-zinc-200"
                data-testid="context-chip-close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {loading ? (
              <div className="p-8 text-center text-[11px] uppercase tracking-[0.3em] text-zinc-500">
                Reading the anchor…
              </div>
            ) : (
              <div className="p-4 space-y-5">
                {/* Summary row */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <Stat label="Exchanges" value={`${detail?.sliding_window_exchanges ?? exchanges}/${cap}`} icon={Anchor} />
                  <Stat label="Tokens" value={`${detail?.session_tokens_total ?? tokens}`} icon={Zap} />
                  <Stat label="Next pulse in" value={`${Math.max(0, interval - ((detail?.session_tokens_total ?? tokens) % interval))}t`} icon={Clock} />
                  <Stat label="Long-term" value={`${detail?.vector_memory_total ?? vectorTotal}`} icon={Brain} />
                </div>

                {/* Anchor */}
                <Section title="1 · Taurian Anchor (system prompt)">
                  <div className="text-[11px] text-emerald-400 uppercase tracking-[0.3em] flex items-center gap-2">
                    <Anchor className="h-3 w-3" /> LOCKED
                  </div>
                  <div className="text-[11px] text-zinc-500 mt-1">
                    projectivy_anchor.txt · {detail?.anchor_bytes ?? "?"} bytes · {detail?.anchor_chunks ?? "?"} chunks
                  </div>
                </Section>

                {/* Sliding window */}
                <Section title={`2 · Sliding Window · last ${detail?.sliding_window_exchanges ?? exchanges} exchanges`}>
                  {(!detail?.sliding_window || detail.sliding_window.length === 0) ? (
                    <div className="text-[11px] text-zinc-600 uppercase tracking-[0.25em]">
                      (empty — no prior turns)
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {detail.sliding_window.map((m, i) => (
                        <div
                          key={`${m.role}-${i}-${(m.content || "").slice(0, 24)}`}
                          data-testid={`context-window-${m.role}-${i}`}
                          className={`border text-[11px] p-2 ${
                            m.role === "user"
                              ? "border-emerald-500/30 bg-emerald-500/5"
                              : "border-zinc-800 bg-zinc-950"
                          }`}
                        >
                          <div className="text-[9px] uppercase tracking-[0.3em] text-zinc-500 mb-1">
                            {m.role === "user" ? "ARCHITECT" : "SENTINEL"}
                          </div>
                          <div className="text-zinc-300 whitespace-pre-wrap">
                            {m.content}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Section>

                {/* Vector recall for THIS turn */}
                <Section title={`3 · Vector Retrieval · ${hitsForDisplay.length} hit${hitsForDisplay.length === 1 ? "" : "s"} for last prompt`}>
                  {hitsForDisplay.length === 0 ? (
                    <div className="text-[11px] text-zinc-600 uppercase tracking-[0.25em]">
                      {(detail?.vector_memory_total ?? vectorTotal) === 0
                        ? "no long-term memories yet — first pulse will seed the archive"
                        : "no semantic matches on the last prompt"}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {hitsForDisplay.map((h, i) => (
                        <div
                          key={h.id || i}
                          data-testid={`context-vector-hit-${i}`}
                          className="border border-zinc-800 bg-zinc-950 p-2 text-[11px]"
                        >
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <div className="flex items-center gap-2 text-[9px] uppercase tracking-[0.3em] text-emerald-400 min-w-0">
                              <Database className="h-3 w-3 shrink-0" />
                              <span>{h.type || "memory"}</span>
                              <span className="text-zinc-500 truncate">{fmtTime(h.timestamp)}</span>
                              {typeof h.distance === "number" && (
                                <span className="text-zinc-500 shrink-0">· d={h.distance.toFixed(3)}</span>
                              )}
                            </div>
                            {isArchitect && h.id && (
                              <button
                                type="button"
                                onClick={() => handleForget(h.id)}
                                disabled={forgetting === h.id}
                                data-testid={`forget-memory-${i}`}
                                title="Forget this memory"
                                className="shrink-0 inline-flex items-center gap-1 px-2 py-1 border border-rose-500/40 text-rose-400 hover:bg-rose-500 hover:text-zinc-950 disabled:opacity-50 text-[9px] uppercase tracking-[0.3em]"
                              >
                                {forgetting === h.id ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  <Trash2 className="h-3 w-3" />
                                )}
                                Forget
                              </button>
                            )}
                          </div>
                          <div className="text-zinc-400 whitespace-pre-wrap">
                            {h.snippet}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Section>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="border border-zinc-800 bg-[#07070a] p-3">
      <div className="text-[9px] uppercase tracking-[0.3em] text-zinc-500 flex items-center gap-1.5">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className="font-heading text-lg mt-1 tabular-nums">{value}</div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">
        {title}
      </div>
      {children}
    </div>
  );
}
